import { get, post } from './API';

export default {
  // List all Payment rows attached to a single booking (both Pattern A
  // via lines.booking_id and Pattern B via counterparty.id). The server
  // returns them sorted by occurred_at desc.
  listForBooking: async (bookingId) => {
    const response = await get('/payments', { booking_id: bookingId });
    return response.data?.data || [];
  },

  // List Payment rows for a counterparty (e.g. partner, operator).
  // Optional date window via { from, to } (ISO strings). Server returns
  // results sorted by occurred_at desc.
  listForCounterparty: async (kind, id, { from, to } = {}) => {
    const response = await get('/payments', {
      counterparty_kind: kind,
      counterparty_id: id,
      from,
      to,
    });
    return response.data?.data;
  },

  // Record a manual Payment for a booking (e.g. wire/cash received).
  // The caller is responsible for assembling the full payload — see
  // RecordWirePaymentModal for the LOCKED shape (direction, method,
  // gross_amount in MAJOR units, currency, occurred_at ISO, etc.).
  recordPayment: async (payload) => {
    const response = await post('/payments', payload);
    return response.data?.data;
  },

  // List Stripe payments that arrived without a `booking_reference` and
  // therefore have not been attached to a booking. The backend models
  // these as `counterparty: { kind: 'system', id: 'stripe-unmatched' }`.
  // Admins reconcile them via the Unmatched Stripe Payments page.
  listUnmatched: async () => {
    const response = await get('/payments', {
      counterparty_kind: 'system',
      counterparty_id: 'stripe-unmatched',
      limit: 200,
    });
    return response.data?.data;
  },

  // Reassign an unmatched payment to a real counterparty (typically a
  // booking). The LOCKED endpoint is POST /payments?action=reassign.
  //
  // Why `params` (the 4th arg to API.post) instead of inlining
  // `?action=reassign` into the URL: the axios wrapper in Services/API.js
  // builds the request URL via `buildUrl(url)` which does not parse a
  // query string, but axios itself merges `config.params` into the URL
  // for us. Using `params` keeps the URL clean, matches how the rest of
  // the codebase composes query strings (see API.js `get`), and avoids
  // a one-off helper change just for this caller.
  // `lines` is optional. When provided (length >= 2), the backend splits
  // the payment across N bookings and writes `payment.lines[]`. When
  // omitted, behaves as the 1:1 reassign.
  reassignPayment: async (paymentId, counterparty, lines) => {
    const body = { payment_id: paymentId, counterparty };
    if (Array.isArray(lines) && lines.length >= 2) {
      body.lines = lines;
    }
    const response = await post(
      '/payments',
      body,
      {},
      { action: 'reassign' },
    );
    return response.data?.data;
  },

  // Re-allocate an already-attached booking-counterparty payment's
  // lines[]. Used to refine where a payment hits (e.g. move a
  // booking-global allocation onto a specific order_line_key, or split
  // an existing allocation across several lines on the same booking).
  // Sum of lines[].gross_amount must equal the payment's gross_amount.
  editPaymentAllocation: async (paymentId, lines) => {
    const response = await post(
      '/payments',
      { payment_id: paymentId, lines },
      {},
      { action: 'edit-allocation' },
    );
    return response.data?.data;
  },

  // Issue a refund against an existing Payment. The backend creates an
  // offset Payment row (direction='out' for an inbound original) and,
  // for a FULL refund of an inbound booking payment, flips
  // Booking.payment.paid back to false unless another inbound completed
  // payment exists. Omit `partial` for a full refund; pass a positive
  // number in major units (e.g. 49.99) for a partial refund.
  refundPayment: async (paymentId, partial) => {
    const body = { payment_id: paymentId };
    if (partial != null) body.partial = partial;
    const response = await post('/payments?action=refund', body);
    return response.data?.data;
  },

  // Generic list endpoint backing the canonical /payments admin page.
  // Accepts any combination of filters (status, direction, method,
  // counterparty_kind, counterparty_id, booking_id, from, to, q, page,
  // limit). The server enforces no "must filter by counterparty_id OR
  // booking_id" rule on this path — calling with an empty object is
  // valid and returns the full ledger (page 1, default limit).
  //
  // Unlike listForBooking/listForCounterparty (which unwrap to the
  // documents array), this returns the FULL envelope so the page can
  // show pagination info: { data: Payment[], pagination: { page, limit,
  // total, pages } }.
  list: async (filters = {}) => {
    // Drop undefined/empty-string keys before passing to get(...) so the
    // URL stays clean (e.g. an empty "from" filter shouldn't become
    // ?from= in the URL — the backend would treat that as a real but
    // empty filter).
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    const response = await get('/payments', params);
    return response.data; // { data, pagination }
  },

  // List outstanding (unpaid) order-line balances for a counterparty
  // over a date window. Used by the Bulk Payment modal to FIFO-allocate
  // a single wire/payout across many bookings at once.
  //
  // LOCKED contract — see CLAUDE.md / the modal docstring. Returns the
  // unwrapped `data` envelope: { lines, total_outstanding, currency,
  // counterparty_kind, counterparty_id, line_direction, period, truncated? }.
  // Lines are pre-sorted by `date` ascending (oldest first) so the UI
  // can walk top-down for FIFO allocation without re-sorting.
  listOutstandingLines: async ({
    counterparty_kind: counterpartyKind,
    counterparty_id: counterpartyId,
    period_from: periodFrom,
    period_to: periodTo,
    line_direction: lineDirection,
    currency,
  }) => {
    const params = {
      action: 'outstanding-lines',
      counterparty_kind: counterpartyKind,
      counterparty_id: counterpartyId,
      period_from: periodFrom,
      period_to: periodTo,
      line_direction: lineDirection,
    };
    if (currency) params.currency = currency;
    const response = await get('/payments', params);
    return response.data?.data;
  },

  // Suggest UNPAID bookings a payment likely belongs to, ranked by how well
  // they match the payment's signals (payer email, amount, date). Used by the
  // reconciliation flows to one-click attach instead of searching. Pass any
  // subset of { amount, currency, date, email, name }; `name` is the payer
  // name off the Stripe billing details and only re-ranks candidates the
  // email/amount bound already admitted — sending it alone returns []. Returns
  // a ranked array of
  // { booking_id, booking_reference, full_name, email_address, date_datetime,
  // price, currency, score, reasons[] }.
  suggestBookings: async ({
    amount, currency, date, email, name, limit,
  } = {}) => {
    const params = Object.fromEntries(
      Object.entries({
        action: 'suggest-bookings', amount, currency, date, email, name, limit,
      }).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    const response = await get('/payments', params);
    return response.data?.data || [];
  },

  // Patch a Payment's notes / bank_reference / occurred_at. At least one
  // of those must be provided. Pass `null` to clear a previously-set
  // value, omit the key to leave it unchanged. The backend echoes back
  // the full updated Payment.
  updatePayment: async (paymentId, patch) => {
    const body = { payment_id: paymentId, ...patch };
    const response = await post(
      '/payments',
      body,
      {},
      { action: 'update' },
    );
    return response.data?.data;
  },
};
